import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import StudentNav from '../components/shared/StudentNav';
import StudentOrgBar from '../components/student/StudentOrgBar';
import { buildStudentLessonListItem } from '../utils/studentLessons';
import { buildStudentLessonPath } from '../utils/studentLessonPath';
import { loadVisibleLessonsForStudent } from '../utils/studentLessonAccess';
import { useStudentContext } from '../hooks/useStudentContext';
import { useStudentLessonProjections } from '../hooks/useStudentModernData';

async function fetchInstructorNames(lessons = []) {
  const entries = await Promise.all(
    Array.from(new Set((lessons || []).map((lesson) => lesson.instructorId).filter(Boolean))).map(
      async (instructorId) => {
        try {
          const userSnap = await getDoc(doc(db, 'users', instructorId));
          return [instructorId, userSnap.exists() ? userSnap.data()?.name || instructorId : instructorId];
        } catch (error) {
          return [instructorId, instructorId];
        }
      }
    )
  );

  return new Map(entries);
}

function StudentLessonsPage() {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [instructorNames, setInstructorNames] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [claimMessage, setClaimMessage] = useState('');
  const [claimError, setClaimError] = useState('');

  // Pass 85.1A.2 — the CANONICAL ACCESS LANE decides the path. MODERN reads lessons via the SAFE PUBLISHED PROJECTIONS
  // ONLY (never the whole lessonInstance). The legacy `loadVisibleLessonsForStudent` (which CLAIMS/LINKS by Auth/email —
  // a WRITE) runs ONLY when lane === 'legacy' (confirmed zero participant profiles). Every UNRESOLVED canonical state
  // (loading / error / needs_org / ambiguous) performs ZERO legacy lesson reads/writes.
  const ctx = useStudentContext(currentUser?.uid || '');
  const lane = ctx.lane;
  const isModern = lane === 'modern';
  const isLegacy = lane === 'legacy';
  const ml = useStudentLessonProjections(isModern ? currentUser?.uid : '', isModern ? ctx.selectedOrganizationId : '', isModern ? ctx.activeParticipantId : '');

  useEffect(() => {
    const loadLessons = async () => {
      if (lane !== 'legacy') return; // the email-claim writer runs ONLY for a confirmed-legacy account
      if (!currentUser) return;

      setLoading(true);
      try {
        const { lessons: visibleLessons, claimedCount, claimError: nextClaimError } =
          await loadVisibleLessonsForStudent(db, currentUser, {
            studentName: userData?.name || '',
          });
        setLessons(visibleLessons);
        setInstructorNames(await fetchInstructorNames(visibleLessons));
        setClaimError(nextClaimError || '');
        setClaimMessage(
          claimedCount > 0
            ? `We found ${claimedCount} lesson${claimedCount === 1 ? '' : 's'} created for your email and linked ${claimedCount === 1 ? 'it' : 'them'} to your account.`
            : ''
        );
      } catch (error) {
        console.error('Error loading student lessons:', error);
        setLessons([]);
        setInstructorNames(new Map());
        setClaimMessage('');
        setClaimError('');
      } finally {
        setLoading(false);
      }
    };

    loadLessons();
  }, [lane, currentUser, userData?.name]);

  const plannedLessons = useMemo(
    () => lessons.filter((lesson) => lesson.status !== 'completed'),
    [lessons]
  );
  const completedLessons = useMemo(
    () => lessons.filter((lesson) => lesson.status === 'completed'),
    [lessons]
  );

  const renderLessonGroup = (groupLessons, title) => (
    <section className="mt-6">
      <h2 className="text-lg font-semibold text-slate-950">{title}</h2>
      {groupLessons.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500">No {title.toLowerCase()} yet.</p>
      ) : (
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          {groupLessons.map((lesson) => {
            const item = buildStudentLessonListItem(lesson, {
              instructorName: instructorNames.get(lesson.instructorId) || '',
            });
            return (
              <button
                key={lesson.lessonId}
                type="button"
                onClick={() => navigate(buildStudentLessonPath({ lessonId: lesson.lessonId }))}
                className="rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm hover:border-slate-300 hover:bg-slate-50"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-950">{item.targetSkillName}</p>
                    <p className="mt-1 text-sm text-slate-600">
                      {item.sportLabel} • {item.trackName}
                    </p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700">
                    {item.statusLabel}
                  </span>
                </div>
                <div className="mt-3 space-y-1 text-sm text-slate-600">
                  <p>{item.dateLabel}</p>
                  <p>Instructor: {item.instructorName}</p>
                  <p>{item.hasLinkedVideo ? 'Linked video review available' : 'Standalone lesson'}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );

  return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8' }} data-student-mode={lane}>
      <StudentNav activeTab="lessons" />
      <div className="max-w-6xl mx-auto p-4">

      <div className="mt-4"><StudentOrgBar ctx={ctx} /></div>

      {isModern ? (
        <section data-testid="lessons-modern" className="mt-2 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Lessons</p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-950">My Lessons</h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">Lessons your coach has shared with you appear here.</p>
          {(ctx.loading || ml.loading) ? (
            <p className="mt-6 text-sm text-slate-500">Loading your lessons...</p>
          ) : ml.error ? (
            <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-5"><p className="text-sm text-red-800">We couldn’t load your lessons right now. This is a loading problem, not an empty list — please try again.</p></div>
          ) : ml.lessons.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-5"><p className="text-sm text-slate-600">No lessons have been shared with you yet. When your coach publishes a lesson summary for you, it will appear here.</p></div>
          ) : (
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {ml.lessons.map((l) => (
                <div key={l.id} className="rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm">
                  <p className="text-sm font-semibold text-slate-950">{l.title || l.lessonDate || 'Lesson'}{l.discipline ? ` · ${l.discipline}` : ''}</p>
                  {l.summary ? <p className="mt-2 text-sm text-slate-600">{String(l.summary).slice(0, 400)}</p> : null}
                  {Array.isArray(l.focusAreas) && l.focusAreas.length ? <p className="mt-2 text-xs text-slate-500">Focus: {l.focusAreas.join(', ')}</p> : null}
                </div>
              ))}
            </div>
          )}
        </section>
      ) : isLegacy ? (
      <section className="mt-2 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Lessons</p>
        <h1 className="mt-2 text-2xl font-semibold text-slate-950">My Lessons</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-600">
          View lessons as their own coaching surface, whether they were created from a video review or planned directly for you by the school.
        </p>
        {claimMessage ? (
          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            {claimMessage}
          </div>
        ) : null}
        {claimError ? (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {claimError}
          </div>
        ) : null}

        {loading ? (
          <p className="mt-6 text-sm text-slate-500">Loading your lessons...</p>
        ) : lessons.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-5">
            <p className="text-sm text-slate-600">
              No lessons are linked to your account yet. Lessons can appear here from uploaded video reviews or from school-created plans tied to your account or email.
            </p>
          </div>
        ) : (
          <>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">All Lessons</p>
                <p className="mt-2 text-3xl font-bold text-slate-950">{lessons.length}</p>
              </div>
              <div className="rounded-2xl bg-indigo-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Planned</p>
                <p className="mt-2 text-3xl font-bold text-indigo-900">{plannedLessons.length}</p>
              </div>
              <div className="rounded-2xl bg-emerald-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-500">Completed</p>
                <p className="mt-2 text-3xl font-bold text-emerald-900">{completedLessons.length}</p>
              </div>
            </div>

            {renderLessonGroup(plannedLessons, 'Planned Lessons')}
            {renderLessonGroup(completedLessons, 'Completed Lessons')}
          </>
        )}
      </section>
      ) : (
      <section data-testid="lessons-lane-state" className="mt-2 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        {lane === 'loading' ? (
          <p className="text-sm text-slate-500">Resolving your coaching profile…</p>
        ) : lane === 'error' ? (
          <p className="text-sm text-red-800">We couldn’t load your coaching right now. Please try again.</p>
        ) : (
          <p className="text-sm text-slate-600">Choose your organisation above to see your lessons.</p>
        )}
      </section>
      )}
      </div>
    </div>
  );
}

export default StudentLessonsPage;
